import { forwardRef } from "react";
import {
  HelperText,
  TextField,
  TextFieldLabel,
  TextFieldTextArea,
} from "@hireart/component-library";
import { useField } from "formik";
import isEmpty from "lodash/isEmpty";
import PropTypes from "prop-types";

/**
 * FormTextArea wires up TextFieldTextArea with Formik
 */
export const FormTextArea = forwardRef(
  (
    {
      "aria-describedby": ariaDescribedBy = undefined,
      className = "",
      id = undefined,
      isDisabled = false,
      isLabelUnstyled = false,
      isOptional = false,
      label,
      name,
      onChange = () => {},
    },
    ref
  ) => {
    const [field, meta] = useField({ name });
    const textAreaId = id || name;
    const hasError = meta.touched && !isEmpty(meta.error);
    const helperTextIds = [
      hasError ? `${textAreaId}-error` : undefined,
      ariaDescribedBy,
    ]
      .filter(Boolean)
      .join(" ");

    const handleChange = (e) => {
      field.onChange(e);
      onChange(e, e.target.value);
    };

    return (
      <div className={className}>
        <TextField
          hasError={hasError}
          isDisabled={isDisabled}
          isOptional={isOptional}
        >
          <TextFieldLabel htmlFor={textAreaId} isUnstyled={isLabelUnstyled}>
            {label}
          </TextFieldLabel>
          <TextFieldTextArea
            {...field}
            ref={ref}
            aria-describedby={
              isEmpty(helperTextIds) ? undefined : helperTextIds
            }
            id={textAreaId}
            name={name}
            onChange={handleChange}
          />
        </TextField>
        {hasError && (
          <HelperText id={`${textAreaId}-error`} hasError>
            {meta.error}
          </HelperText>
        )}
      </div>
    );
  }
);

FormTextArea.displayName = "FormTextArea";

FormTextArea.propTypes = {
  "aria-describedby": PropTypes.string,
  className: PropTypes.string,
  id: PropTypes.string,
  isDisabled: PropTypes.bool,
  isLabelUnstyled: PropTypes.bool,
  isOptional: PropTypes.bool,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func,
};
